import moment from 'moment';

import { Employees } from '~/data/employees';
import { OrganizationUnit } from '~/data/orgunits';
import { WorkExperienceReportData } from '~/data/reports';
import { convertToDateOrUndefined } from '~/utils/helpers/date';

export type WorkExperienceReportRow = {
  employeeId: number;
  firstName: string;
  lastName: string;
  unitName: string;
  unitNumber: string;
  manager: string;
  originalStartDate?: Date;
  startDate: Date;
  totalExperienceDays: number;
  totalExperienceYearsAndMonths: string;
  hasExternalExperience: boolean;
  hasDeductedLongAbsences: boolean;
};

export const processReportData = (
  data: WorkExperienceReportData[],
  getEmployeeById: (employeeId?: number | undefined) => Employees | undefined,
  getUnitById: (unitId?: number) => OrganizationUnit | undefined,
  formatTotalYearsAndMonths: (years: number, months: number) => string,
  reportParams: { deductAbsences: boolean; extExperience: boolean },
): WorkExperienceReportRow[] => {
  const { deductAbsences, extExperience } = reportParams;

  return data.map((entry): WorkExperienceReportRow => {
    const {
      fEmpId,
      fEmpOrigStartDate,
      fEmpStartDate,
      fEmpTotalDays,
      fEmpTotalYears,
      fEmpTotalMonthsRemaining,
      fEmpCurrentAbsLong,
      fEmpPreviousCompany,
      fEmpPreviousAbsLong,
    } = entry;

    const employeeInfo = getEmployeeById(fEmpId);

    const unitInfo = getUnitById(employeeInfo?.fEmpUnit);

    const managerInfo = getEmployeeById(employeeInfo?.fEmpManagerId);
    const managerName = !!managerInfo
      ? `${managerInfo.fEmpLastName}, ${managerInfo.fEmpFirstName}`
      : '';

    const hasCurrentLongAbsences = fEmpCurrentAbsLong.length > 0;
    const hasExternalExperience = fEmpPreviousCompany.length > 0;
    const hasExternalLongAbsences = fEmpPreviousAbsLong.length > 0;
    const hasDeductedLongAbsences =
      (deductAbsences && hasCurrentLongAbsences) ||
      (extExperience && hasExternalLongAbsences);

    return {
      employeeId: fEmpId,
      firstName: employeeInfo?.fEmpFirstName ?? '',
      lastName: employeeInfo?.fEmpLastName ?? '',
      unitName: unitInfo?.fOrgUnitName ?? '',
      unitNumber: unitInfo?.fOrgUnitNumber ?? '',
      manager: managerName,
      originalStartDate: convertToDateOrUndefined(fEmpOrigStartDate),
      startDate: moment(fEmpStartDate).toDate(),
      totalExperienceDays: fEmpTotalDays,
      totalExperienceYearsAndMonths: formatTotalYearsAndMonths(
        fEmpTotalYears,
        fEmpTotalMonthsRemaining,
      ),
      hasExternalExperience,
      hasDeductedLongAbsences,
    };
  });
};
